import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, Users, Target, BarChart3, Shield, Brain, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const AIForCROs = () => {
  const scrollToDemo = () => {
    window.location.href = '/?scrollTo=demo';
  };

  return (
    <Layout>
      <SEOHead
        title="AI for Chief Revenue Officers (CROs): AI Chief of Staff for Revenue Leaders | NoQ5"
        description="NoQ5 is the AI Chief of Staff for Chief Revenue Officers. Evidence-based forecasts, pipeline risk alerts, team performance insights and a Monday Morning Digest. Built for B2B revenue leadership, not pharma research."
        keywords="AI for Chief Revenue Officer, AI for CRO sales, Chief Revenue Officer AI tool, AI chief revenue officer, CRO dashboard AI, revenue leader AI, AI pipeline visibility CRO, AI forecasting Chief Revenue Officer, revenue operations AI, CRO software, CRO AI assistant"
        canonicalUrl="https://noq5.ai/ai-for-cros"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "AI for CROs - AI Chief of Staff for Chief Revenue Officers",
          "description": "NoQ5 is the AI Chief of Staff for CROs. Evidence-based forecasts, pipeline risk alerts, and team performance insights.",
          "url": "https://noq5.ai/ai-for-cros",
          "publisher": { "@type": "Organization", "name": "NoQ5.ai", "url": "https://noq5.ai" }
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What AI tools do CROs need?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "CROs need AI that delivers three things: accurate forecasts they can defend to the board, real-time pipeline visibility without chasing reps for updates, and early warning signals on deals going dark. NoQ5 provides all three in one platform with evidence-based scoring."
              }
            },
            {
              "@type": "Question",
              "name": "How does AI improve forecast accuracy for CROs?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Traditional forecasts rely on rep self-reporting and are wrong 20-40% of the time. NoQ5 scores every deal using 15+ buyer engagement signals from email, calendar, and CRM activity. This evidence-based approach improves forecast accuracy by 30-50% in the first quarter."
              }
            }
          ]
        }]}
      />

      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/8 border border-primary/15 mb-6">
              <Target className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground/80">Built for Revenue Leadership</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              AI for Chief Revenue Officers<br />Your AI Chief of Staff
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Built for B2B revenue leadership. Stop chasing reps for updates, stop guessing your forecast, stop losing deals in the dark. NoQ5 gives the Chief Revenue Officer complete revenue visibility, powered by real buyer evidence, not opinions.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl" onClick={scrollToDemo}>
              Talk to Sales <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>

          {/* CRO Pain Points */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">The CRO Challenges NoQ5 Solves</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { problem: 'Forecasts miss by 20-40%', solution: 'Evidence-based forecasting from real buyer signals. Every number has a trail.', icon: BarChart3 },
                { problem: 'Pipeline reviews are backward-looking', solution: 'Real-time deal health scores updated every hour with proactive risk alerts.', icon: Zap },
                { problem: 'CRM data is 70% incomplete', solution: 'Autonomous CRM updates from meetings, emails, and calls. 95%+ data completeness.', icon: Shield },
                { problem: 'Rep turnover kills pipeline', solution: 'Every deal interaction is captured and transferable. Zero knowledge loss on transitions.', icon: Users },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-card border border-border rounded-xl"
                >
                  <item.icon className="w-8 h-8 text-primary mb-4" />
                  <div className="text-destructive/80 text-sm font-medium mb-2">Problem: {item.problem}</div>
                  <p className="text-foreground font-medium">{item.solution}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Monday Morning Digest */}
          <section className="mb-20 p-8 bg-card border border-border rounded-2xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-4">Monday Morning Digest</h2>
            <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto">
              Every Monday, NoQ5 delivers a comprehensive revenue digest to your inbox. Pipeline movement, deals at risk, key wins, rep performance, and recommended actions. Start every week with complete visibility.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                'Pipeline movement vs. last week',
                'Deals going dark (no buyer response in 7+ days)',
                'Forecast changes with evidence',
                'Top deals progressing and why',
                'Rep activity and engagement gaps',
                'Recommended CRO actions for the week',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-3">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Who This Is For */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-foreground text-center mb-8">Built for Revenue Leaders</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { title: 'Chief Revenue Officers', desc: 'Board-ready forecasts, pipeline intelligence, and team performance in one view.' },
                { title: 'VP of Sales', desc: 'Real-time pipeline visibility, coaching insights, and deal risk alerts across your team.' },
                { title: 'Revenue Operations', desc: 'Clean CRM data, automated workflows, and intelligence infrastructure without custom builds.' },
              ].map((persona, i) => (
                <div key={i} className="p-6 bg-card border border-border rounded-xl text-center">
                  <h3 className="font-semibold text-foreground mb-2">{persona.title}</h3>
                  <p className="text-sm text-muted-foreground">{persona.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal Links */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-foreground text-center mb-8">Explore More</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/product/command-center" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">Command Center</h3>
                <p className="text-sm text-muted-foreground">The CRO war room</p>
              </Link>
              <Link to="/use-cases/deal-forecasting" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">AI Forecasting</h3>
                <p className="text-sm text-muted-foreground">Evidence-based predictions</p>
              </Link>
              <Link to="/use-cases/pipeline-management" className="p-4 bg-card border border-border rounded-xl hover:border-primary/40 transition-colors text-center">
                <h3 className="font-semibold text-foreground mb-1">Pipeline Intelligence</h3>
                <p className="text-sm text-muted-foreground">Real-time deal health</p>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <div className="text-center p-12 bg-primary/5 border border-primary/20 rounded-2xl">
            <h2 className="text-3xl font-bold text-foreground mb-4">Your AI Chief of Staff Is Ready</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Join CROs who have stopped guessing and started leading with evidence.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl" onClick={scrollToDemo}>
              Talk to Sales <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AIForCROs;
